import React, { useState, useEffect } from 'react';
import { 
  BankSearch, 
  BankSearchPool, 
  BankSearchWeights, 
  ResultsOverride 
} from '../../../../utils/types/candi_bank_types';
import { SkillChipRow } from './SkillChipRow';
import { WeightSliders } from './WeightSliders';

interface BankSearchEditorProps {
  search: BankSearch;
  pool: BankSearchPool | null;
  isReindexing: boolean;
  onPatchSkills: (required: string[], mandatory: string[], desirable: string[]) => Promise<void>;
  onPatchJobTitle: (jobTitle: string) => Promise<void>;
  onPatchEducations: (educations: string[]) => Promise<void>;
  onPatchCertifications: (certifications: string[]) => Promise<void>;
  onPatchRemoveMatches: (skill: string, matches: string[]) => Promise<void>;
  onPatchExperience: (min: number, require: boolean) => Promise<void>;
  onPatchTitle: (title: string) => Promise<void>;
  onRefreshFromOpening: () => Promise<void>;
  weightOverride: Partial<BankSearchWeights> | null;
  onWeightOverrideChange: (override: Partial<BankSearchWeights> | null) => void;
  onCommitWeights: () => Promise<void>;
  onResetWeights: () => void;
  onApplyWeightOverride: (override: ResultsOverride) => void;
}

export const BankSearchEditor: React.FC<BankSearchEditorProps> = ({
  search,
  pool,
  isReindexing,
  onPatchSkills,
  onPatchJobTitle,
  onPatchEducations,
  onPatchCertifications,
  onPatchRemoveMatches,
  onPatchExperience,
  onPatchTitle,
  onRefreshFromOpening,
  weightOverride,
  onWeightOverrideChange,
  onCommitWeights,
  onResetWeights,
  onApplyWeightOverride,
}) => {
  const [title, setTitle] = useState(search.title || '');
  const [jobTitle, setJobTitle] = useState(search.jobTitle || '');
  const [isCriteriaExpanded, setIsCriteriaExpanded] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    setTitle(search.title || '');
  }, [search.title]);

  useEffect(() => {
    setJobTitle(search.jobTitle || '');
  }, [search.jobTitle]);

  const handleTitleBlur = () => {
    if (title !== (search.title || '') && title.trim() !== '') {
      onPatchTitle(title.trim());
    }
  };

  const handleJobTitleBlur = () => {
    if (jobTitle !== (search.jobTitle || '')) {
      onPatchJobTitle(jobTitle.trim());
    }
  };

  const handleSkillsChange = (tier: 'required' | 'mandatory' | 'desirable', newSkills: string[]) => {
    const r = tier === 'required' ? newSkills : search.requiredSkills;
    const m = tier === 'mandatory' ? newSkills : search.mandatorySkills;
    const d = tier === 'desirable' ? newSkills : search.desirableSkills;
    onPatchSkills(r, m, d);
  };

  const handleMoveTo = (skill: string, sourceTier: 'required' | 'mandatory' | 'desirable', targetTierKey: string) => {
    let r = [...search.requiredSkills];
    let m = [...search.mandatorySkills];
    let d = [...search.desirableSkills];

    if (sourceTier === 'required') r = r.filter(s => s !== skill);
    if (sourceTier === 'mandatory') m = m.filter(s => s !== skill);
    if (sourceTier === 'desirable') d = d.filter(s => s !== skill);

    if (targetTierKey === 'required' && !r.includes(skill)) r.push(skill);
    if (targetTierKey === 'mandatory' && !m.includes(skill)) m.push(skill);
    if (targetTierKey === 'desirable' && !d.includes(skill)) d.push(skill);

    onPatchSkills(r, m, d);
  };

  const coverage = pool?.coverage || {};
  const skillMatches = search.skillMatches || [];
  const totalSkillsCount = (search.requiredSkills?.length || 0) + (search.mandatorySkills?.length || 0) + (search.desirableSkills?.length || 0);

  return (
    <div className='bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden transition-all'>
      {/* Top compact header bar */}
      <div className='px-4 py-2.5 bg-slate-50/60 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
          <input 
            type='text' 
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            className='text-sm font-bold text-slate-800 outline-none hover:bg-white focus:bg-white px-2 py-0.5 rounded-md border border-transparent hover:border-slate-200 focus:border-indigo-500 transition-all w-full max-w-sm'
            placeholder='Search Title'
          />
          {isReindexing && (
            <span className='px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-extrabold animate-pulse uppercase tracking-wider flex-shrink-0'>
              Re-indexing
            </span>
          )}
        </div>

        <div className='flex items-center gap-2'>
          {search.source === 'opening' && (
            <button
              onClick={() => {
                if (window.confirm('This discards your edits. Continue?')) {
                  onRefreshFromOpening();
                }
              }}
              className='text-xs font-semibold text-slate-600 hover:text-indigo-600 transition-colors bg-white hover:bg-indigo-50 px-2.5 py-1 rounded-lg border border-slate-200 shadow-2xs flex items-center gap-1.5'
              title='Refresh from job opening'
            >
              <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                <path d='M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2'/>
              </svg>
              Refresh
            </button>
          )}

          {/* Criteria Collapse / Expand Button */}
          <button
            type='button'
            onClick={() => setIsCriteriaExpanded(!isCriteriaExpanded)}
            className='text-xs font-bold text-slate-500 hover:text-slate-800 bg-white hover:bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200 shadow-2xs transition-all flex items-center gap-1'
          >
            <span>{isCriteriaExpanded ? 'Collapse Criteria' : `Criteria (${totalSkillsCount} skills)`}</span>
            <svg className={`w-3.5 h-3.5 transition-transform ${isCriteriaExpanded ? 'rotate-180' : ''}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <polyline points='6 9 12 15 18 9' />
            </svg>
          </button>
        </div>
      </div>

      {/* Collapsible Search Criteria Section */}
      {isCriteriaExpanded && (
        <div className='p-4 space-y-3'>
          {/* Quick inline controls: Target Job Title & Experience on single strip */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3 pb-3 border-b border-slate-100'>
            {/* Target Job Title */}
            <div className='flex items-center gap-2'>
              <label className='text-xs font-bold text-slate-500 whitespace-nowrap'>Role Title:</label>
              <input
                type='text'
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                onBlur={handleJobTitleBlur}
                className='flex-1 text-xs font-medium border border-slate-200 rounded-lg px-2.5 py-1 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50/50 hover:bg-white transition-colors'
                placeholder='e.g. Senior Software Engineer'
              />
            </div>

            {/* Experience Controls */}
            <div className='flex items-center gap-3 md:justify-end'>
              <div className='flex items-center gap-1.5'>
                <label className='text-xs font-bold text-slate-500'>Min Exp:</label>
                <input
                  type='number'
                  min='0'
                  value={search.minimumWorkExperience || 0}
                  onChange={(e) => onPatchExperience(parseInt(e.target.value) || 0, search.requireExperience)}
                  className='w-14 text-xs font-bold border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-indigo-500 text-center bg-slate-50/50 hover:bg-white transition-colors'
                />
                <span className='text-xs font-medium text-slate-500'>yrs</span>
              </div>

              <label className='flex items-center gap-1.5 cursor-pointer bg-slate-50 hover:bg-slate-100 px-2 py-1 rounded-lg border border-slate-200 text-xs transition-colors select-none'>
                <input
                  type='checkbox'
                  checked={search.requireExperience}
                  onChange={(e) => onPatchExperience(search.minimumWorkExperience || 0, e.target.checked)}
                  className='w-3.5 h-3.5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer'
                />
                <span className='text-xs font-semibold text-slate-700'>Strictly Require</span>
              </label>
            </div>
          </div>

          {/* Skill Tier Rows */}
          <div className='space-y-1'>
            <SkillChipRow
              label='Must Have'
              emptyHint='Empty by default — add a skill here to exclude candidates who lack it.'
              badgeBg='bg-red-50'
              badgeText='text-red-700'
              skills={search.requiredSkills}
              skillMatches={skillMatches}
              coverage={coverage}
              tiers={[
                { label: 'Important', key: 'mandatory' },
                { label: 'Nice to Have', key: 'desirable' },
              ]}
              onSkillsChange={(s) => handleSkillsChange('required', s)}
              onMoveTo={(skill, target) => handleMoveTo(skill, 'required', target)}
              onRemoveMatch={(skill, match) => onPatchRemoveMatches(skill, [match])}
              isReindexing={isReindexing}
            />
            
            <SkillChipRow
              label='Important'
              badgeBg='bg-amber-50'
              badgeText='text-amber-700'
              skills={search.mandatorySkills}
              skillMatches={skillMatches}
              coverage={coverage}
              tiers={[
                { label: 'Must Have', key: 'required' },
                { label: 'Nice to Have', key: 'desirable' },
              ]}
              onSkillsChange={(s) => handleSkillsChange('mandatory', s)}
              onMoveTo={(skill, target) => handleMoveTo(skill, 'mandatory', target)}
              onRemoveMatch={(skill, match) => onPatchRemoveMatches(skill, [match])}
              isReindexing={isReindexing}
            />
            
            <SkillChipRow
              label='Nice to Have'
              badgeBg='bg-blue-50'
              badgeText='text-blue-700'
              skills={search.desirableSkills}
              skillMatches={skillMatches}
              coverage={coverage}
              tiers={[
                { label: 'Must Have', key: 'required' },
                { label: 'Important', key: 'mandatory' },
              ]}
              onSkillsChange={(s) => handleSkillsChange('desirable', s)}
              onMoveTo={(skill, target) => handleMoveTo(skill, 'desirable', target)}
              onRemoveMatch={(skill, match) => onPatchRemoveMatches(skill, [match])}
              isReindexing={isReindexing}
            />
          </div>

          {/* Advanced Weights Disclosure */}
          <div className='pt-2 border-t border-slate-100'>
            <button 
              type='button'
              onClick={() => setAdvancedOpen(!advancedOpen)}
              className='inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-indigo-600 transition-colors'
            >
              <svg className={`w-3.5 h-3.5 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <polyline points='6 9 12 15 18 9' />
              </svg>
              <span>{advancedOpen ? 'Hide Weight Weights' : 'Adjust Scoring Weights'}</span>
            </button>
            
            {advancedOpen && (
              <div className='mt-2 bg-slate-50/80 p-3 rounded-xl border border-slate-200/80 animate-in fade-in zoom-in-95 duration-150'>
                <WeightSliders
                  weights={search.weights}
                  override={weightOverride}
                  onOverrideChange={(newOverride) => {
                    onWeightOverrideChange(newOverride);
                    onApplyWeightOverride({
                      weights: {
                        semantic: newOverride.semantic,
                        mandatory_skills: newOverride.mandatorySkills,
                        desirable_skills: newOverride.desirableSkills,
                        experience: newOverride.experience,
                      },
                    });
                  }}
                  onCommit={onCommitWeights}
                  onReset={onResetWeights}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
